/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The canvas viewport: a dotted board the flow sits on that you can move
 * around and zoom, like a whiteboard.
 *
 * - Drag the empty board to pan; on wide screens, where the board fills the
 *   window, scroll (or a two-finger swipe) pans too. Narrower, the page
 *   scrolls as usual.
 * - Ctrl/Cmd + scroll, or a trackpad pinch, zooms towards the pointer.
 * - The buttons zoom, fit the whole flow, or go back to 100%.
 * - A step dropped anywhere on the board that is not a "+" or a card is
 *   added at the end of the flow.
 *
 * Panning only starts on the board itself, so cards, buttons and the
 * browser's own drag and drop of steps work as before.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Maximize2, Minus, Plus } from "lucide-react";
import { v2 as t } from "styles/tokens/v2";
import { DRAG_TYPE } from "./stepKinds";

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
const PAD = 32;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +z.toFixed(2)));

// What the pointer can press without starting a pan.
const INTERACTIVE = "button, a, input, textarea, select, [role='button'], [draggable='true'], .MuiMenu-root";

export default function CanvasViewport({ children, onDropAnywhere, sx }) {
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: PAD, zoom: 1 });
  const [panning, setPanning] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const pan = useRef(null);
  const placed = useRef(false);

  const sizes = () => {
    const vp = viewportRef.current;
    const st = stageRef.current;
    if (!vp || !st) return null;
    return { vw: vp.clientWidth, vh: vp.clientHeight, sw: st.offsetWidth, sh: st.offsetHeight };
  };

  /** Zoom to `zoom`, keeping the board point under (cx, cy) where it is. */
  const zoomAt = useCallback((zoom, cx, cy) => {
    setView((v) => {
      const z = clampZoom(zoom);
      const px = cx ?? (viewportRef.current?.clientWidth || 0) / 2;
      const py = cy ?? (viewportRef.current?.clientHeight || 0) / 2;
      return { zoom: z, x: px - ((px - v.x) * z) / v.zoom, y: py - ((py - v.y) * z) / v.zoom };
    });
  }, []);

  const centre = useCallback((zoom = 1) => {
    const s = sizes();
    if (!s) return;
    setView({ zoom, x: (s.vw - s.sw * zoom) / 2, y: PAD });
  }, []);

  const fit = useCallback(() => {
    const s = sizes();
    if (!s || !s.sw || !s.sh) return;
    const zoom = clampZoom(Math.min(1, (s.vw - PAD * 2) / s.sw, (s.vh - PAD * 2) / s.sh));
    setView({ zoom, x: (s.vw - s.sw * zoom) / 2, y: Math.max(PAD, (s.vh - s.sh * zoom) / 2) });
  }, []);

  // Start centred at 100% once the flow has a size.
  useLayoutEffect(() => {
    if (placed.current) return;
    const s = sizes();
    if (s && s.sw) {
      placed.current = true;
      centre(1);
    }
  });

  // Wheel needs a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const wide = window.matchMedia?.("(min-width: 1200px)");
    const onWheel = (e) => {
      const zooming = e.ctrlKey || e.metaKey;
      if (!zooming && wide && !wide.matches) return;
      e.preventDefault();
      if (zooming) {
        const rect = vp.getBoundingClientRect();
        setView((v) => {
          const z = clampZoom(v.zoom * Math.exp(-e.deltaY * 0.01));
          const px = e.clientX - rect.left;
          const py = e.clientY - rect.top;
          return { zoom: z, x: px - ((px - v.x) * z) / v.zoom, y: py - ((py - v.y) * z) / v.zoom };
        });
      } else {
        setView((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
      }
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, []);

  // A drag that ends anywhere (dropped, cancelled with Esc) clears the outline.
  useEffect(() => {
    const clear = () => setDropOver(false);
    document.addEventListener("dragend", clear);
    document.addEventListener("drop", clear);
    return () => {
      document.removeEventListener("dragend", clear);
      document.removeEventListener("drop", clear);
    };
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    if (e.button === 0 && e.target.closest(INTERACTIVE)) return;
    pan.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, x: view.x, y: view.y };
    e.currentTarget.setPointerCapture(e.pointerId);
    setPanning(true);
  };
  const onPointerMove = (e) => {
    const p = pan.current;
    if (!p || p.id !== e.pointerId) return;
    setView((v) => ({ ...v, x: p.x + e.clientX - p.sx, y: p.y + e.clientY - p.sy }));
  };
  const endPan = (e) => {
    if (!pan.current || pan.current.id !== e.pointerId) return;
    pan.current = null;
    setPanning(false);
  };

  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    const step = e.shiftKey ? 120 : 40;
    const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) {
      e.preventDefault();
      const [dx, dy] = moves[e.key];
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    } else if (e.key === "+" || e.key === "=") zoomAt(view.zoom + 0.1);
    else if (e.key === "-") zoomAt(view.zoom - 0.1);
    else if (e.key === "0") centre(1);
  };

  return (
    <Box
      ref={viewportRef}
      component="section"
      aria-label="Automation flow. Drag the board or use the arrow keys to move around; plus and minus zoom."
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onKeyDown={onKeyDown}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
        e.preventDefault();
        if (!dropOver) setDropOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false);
      }}
      // Capture runs before a "+" or a card stops the drop going further.
      onDropCapture={() => setDropOver(false)}
      onDrop={(e) => {
        const raw = e.dataTransfer.getData(DRAG_TYPE);
        if (!raw) return;
        e.preventDefault();
        try {
          onDropAnywhere(JSON.parse(raw));
        } catch (error) {
          // A malformed drag is ignored.
        }
      }}
      sx={{
        position: "relative",
        overflow: "hidden",
        touchAction: "none",
        userSelect: panning ? "none" : undefined,
        cursor: panning ? "grabbing" : "grab",
        backgroundImage: `radial-gradient(${t.border2} ${Math.max(0.6, view.zoom)}px, transparent ${Math.max(0.6, view.zoom)}px)`,
        backgroundSize: `${18 * view.zoom}px ${18 * view.zoom}px`,
        backgroundPosition: `${view.x}px ${view.y}px`,
        outline: dropOver ? `2px dashed ${t.primary}` : "none",
        outlineOffset: -2,
        "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: -2 },
        ...sx,
      }}
    >
      <Box
        ref={stageRef}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "max-content",
          minWidth: 420,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
          transformOrigin: "0 0",
          cursor: "default",
        }}
      >
        {children}
      </Box>

      <Box
        sx={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 0.5, zIndex: 1, bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "8px", p: 0.25, cursor: "default" }}
      >
        <Tooltip title="Zoom out">
          <IconButton size="small" aria-label="Zoom out" onClick={() => zoomAt(view.zoom - 0.1)} disabled={view.zoom <= MIN_ZOOM}><Minus size={15} /></IconButton>
        </Tooltip>
        <Tooltip title="Back to 100%">
          <ButtonBase aria-label={`Zoom ${Math.round(view.zoom * 100)}%. Reset to 100%`} onClick={() => centre(1)} sx={{ fontSize: 12.5, fontFamily: "inherit", color: t.text2, minWidth: 40, borderRadius: "6px", "&:hover": { bgcolor: t.hover } }}>
            {Math.round(view.zoom * 100)}%
          </ButtonBase>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" aria-label="Zoom in" onClick={() => zoomAt(view.zoom + 0.1)} disabled={view.zoom >= MAX_ZOOM}><Plus size={15} /></IconButton>
        </Tooltip>
        <Tooltip title="Fit the whole flow">
          <IconButton size="small" aria-label="Fit the whole flow" onClick={fit}><Maximize2 size={14} /></IconButton>
        </Tooltip>
      </Box>

      <Typography aria-hidden="true" sx={{ position: "absolute", left: 12, bottom: 8, fontSize: 12, color: t.text3, pointerEvents: "none", bgcolor: t.surface, px: 0.75, borderRadius: "4px", whiteSpace: "nowrap" }}>
        Drag the board to move · Ctrl/⌘ + scroll to zoom
      </Typography>
    </Box>
  );
}

CanvasViewport.propTypes = {
  children: PropTypes.node.isRequired,
  onDropAnywhere: PropTypes.func.isRequired,
  sx: PropTypes.object,
};
CanvasViewport.defaultProps = { sx: {} };
